/**
 * Factory functions for creating API clients
 */
import { KoraClient } from './clients/kora-client.js';
import { SecretOrgClient } from './clients/secret-org-client.js';
import { MingleMageClient } from './clients/mingle-mage-client.js';
import { MingleMageFactoryAdminClient } from './clients/mingle-mage-factory-admin-client.js';
import { AtlasClient } from './clients/atlas-client.js';
import { AtlasCurationClient } from './clients/atlas-curation-client.js';
import { getKoraApiUrl, getMingleMageApiUrl, getAtlasApiUrl, getEnvironment } from '@architeia/auth';
export function createDefaultRetryConfig() {
    return {
        retries: 3,
        retryDelay: retryCount => Math.min(1000 * Math.pow(2, retryCount), 10000)
    };
}
/**
 * Resolve Kora base URL: explicit config → build-time VITE_KORA_API_URL → env from MODE.
 * Deployed SPAs are built with MODE=production but VITE_KORA_API_URL selects dev/staging/prod API.
 */
function resolveKoraBaseURL(configBaseURL) {
    if (configBaseURL)
        return configBaseURL;
    const viteUrl = typeof import.meta !== 'undefined' && import.meta.env?.VITE_KORA_API_URL;
    if (typeof viteUrl === 'string' && viteUrl.length > 0) {
        const stripped = viteUrl.trim().replace(/\/+$/, '');
        if (stripped)
            return stripped;
    }
    return getKoraApiUrl(getEnvironment());
}
/**
 * Create Kora API client with default configuration
 *
 * @example
 * ```ts
 * import { useAuth0, getDefaultAuth0Config } from '@architeia/auth'
 *
 * const { getAccessToken } = useAuth0(getDefaultAuth0Config())
 * const client = createKoraClient({ getAccessToken })
 * ```
 */
export function createKoraClient(config) {
    const baseURL = resolveKoraBaseURL(config.baseURL);
    return new KoraClient({
        baseURL,
        timeout: config.timeout,
        getAccessToken: config.getAccessToken,
        retry: config.retry || createDefaultRetryConfig(),
        logger: config.logger
    });
}
/**
 * Resolve Secret Org API base URL: explicit config → VITE_SECRET_ORG_API_URL → dev default
 * → '' (relative, same-origin).
 *
 * Secret Org's primary consumer is the SAME-ORIGIN SPA apps/secret-org-web (SPA and /api/v1
 * share an origin — see services/secret-org server). The '' relative fallback is used in
 * prod/staging on purpose and is robust to environment-detection failure.
 *
 * NOTE (KORA-39): a Kora-symmetric getSecretOrgApiUrl() now exists in @architeia/auth, but it
 * is deliberately NOT used here — it would emit a cross-origin absolute URL and break the
 * same-origin SPA (and trigger browser "local network" prompts on the deployed HTTPS site).
 * The helper is for cross-origin consumers; there is none today (apps/web-dashboard's performer
 * editor now talks to Kora itself, KORA-132, and no longer calls the Secret Org).
 */
function resolveSecretOrgBaseURL(configBaseURL) {
    if (configBaseURL !== undefined && configBaseURL !== '')
        return configBaseURL;
    const viteUrl = typeof import.meta !== 'undefined' && import.meta.env?.VITE_SECRET_ORG_API_URL;
    if (typeof viteUrl === 'string' && viteUrl.length > 0) {
        return viteUrl.trim().replace(/\/+$/, '');
    }
    // secret-org-web Vite dev: proxy /api → Secret Org service (see apps/secret-org-web/vite.config.ts)
    if (typeof import.meta !== 'undefined' &&
        import.meta.env?.DEV &&
        import.meta.env?.VITE_SECRET_ORG_DEV_PROXY === 'true') {
        return '';
    }
    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
        return 'http://localhost:3001';
    }
    return '';
}
/**
 * Create Secret Org API client (Library, workspaces, repertoire, Irene).
 */
export function createSecretOrgClient(config) {
    const baseURL = resolveSecretOrgBaseURL(config.baseURL);
    return new SecretOrgClient({
        baseURL,
        timeout: config.timeout,
        getAccessToken: config.getAccessToken,
        retry: config.retry || createDefaultRetryConfig(),
        logger: config.logger
    });
}
/**
 * Resolve Mingle Mage API base URL: explicit config → VITE_MINGLE_MAGE_API_URL →
 * per-environment host via the shared @architeia/auth helper (KORA-39).
 *
 * Mingle Mage is NOT behind the api.{env}.pinakes.de gateway — it is served from its own
 * host (dev/staging: minglemage.{env}.pinakes.de; prod: minglemage.com). There is no
 * hardcoded localhost default (matching resolveKoraBaseURL). For local development against
 * the service on :3004, set VITE_MINGLE_MAGE_API_URL=http://localhost:3004.
 */
export function resolveMingleMageBaseURL(configBaseURL) {
    if (configBaseURL !== undefined && configBaseURL !== '')
        return configBaseURL;
    const viteUrl = typeof import.meta !== 'undefined' && import.meta.env?.VITE_MINGLE_MAGE_API_URL;
    if (typeof viteUrl === 'string' && viteUrl.length > 0) {
        const stripped = viteUrl.trim().replace(/\/+$/, '');
        if (stripped)
            return stripped;
    }
    return getMingleMageApiUrl(getEnvironment());
}
/**
 * Create Mingle Mage API client (sequence shares, future Mingle Mage APIs).
 */
export function createMingleMageClient(config) {
    const baseURL = resolveMingleMageBaseURL(config.baseURL);
    return new MingleMageClient({
        baseURL,
        timeout: config.timeout,
        getAccessToken: config.getAccessToken,
        retry: config.retry || createDefaultRetryConfig(),
        logger: config.logger
    });
}
/**
 * Create the Mingle Mage factory catalog administration client (MM-727, Admin Panel only).
 */
export function createMingleMageFactoryAdminClient(config) {
    return new MingleMageFactoryAdminClient({
        baseURL: resolveMingleMageBaseURL(config.baseURL),
        timeout: config.timeout,
        getAccessToken: config.getAccessToken,
        retry: config.retry || createDefaultRetryConfig(),
        logger: config.logger
    });
}
/**
 * Resolve Atlas base URL (ATLAS-7 D8): explicit config → `VITE_ATLAS_API_URL` → `''` (relative)
 * ONLY when both DEV and the opt-in `VITE_ATLAS_DEV_PROXY` flag are set → `getAtlasApiUrl`.
 *
 * The proxy fallback is opt-in (unlike Secret Org's unconditional dev default), because a future
 * cross-origin DEV consumer of this shared client — the Secret Org `AtlasTrickEmbed`
 * (ENGAGEMENT_LAYER.md) — must NOT silently get a same-origin-relative URL; only
 * `apps/conjuring-atlas`'s own dev server, which proxies `/api` itself (`vite.config.ts`), sets
 * the flag.
 */
function resolveAtlasBaseURL(configBaseURL) {
    if (configBaseURL !== undefined && configBaseURL !== '')
        return configBaseURL;
    const viteUrl = typeof import.meta !== 'undefined' && import.meta.env?.VITE_ATLAS_API_URL;
    if (typeof viteUrl === 'string' && viteUrl.length > 0) {
        return viteUrl.trim().replace(/\/+$/, '');
    }
    if (typeof import.meta !== 'undefined' &&
        import.meta.env?.DEV &&
        import.meta.env?.VITE_ATLAS_DEV_PROXY === 'true') {
        return '';
    }
    return getAtlasApiUrl(getEnvironment());
}
/**
 * Create Atlas API client (public, read-only catalog — no `getAccessToken`, the API sends no
 * token). Default retry is `{ retries: 1, retryDelay: 500 }`: the stock 3x exponential backoff
 * used elsewhere would delay an "unavailable" state by roughly 7s, too slow for a public read.
 */
export function createAtlasClient(config) {
    const baseURL = resolveAtlasBaseURL(config?.baseURL);
    return new AtlasClient({
        baseURL,
        timeout: config?.timeout,
        retry: config?.retry || { retries: 1, retryDelay: 500 },
        logger: config?.logger
    });
}
/**
 * Create the authenticated Atlas curation client (ATLAS-15). `getAccessToken` is required: every
 * curation route authenticates. Base URL resolution is shared with `createAtlasClient`, so
 * `VITE_ATLAS_API_URL` / the opt-in `VITE_ATLAS_DEV_PROXY` behave identically. Default retry is
 * `{ retries: 1, retryDelay: 500 }` for GETs; the client itself never retries a write, and never
 * retries a 429 (`RATE_LIMITED`), whatever `retry` config is passed here.
 */
export function createAtlasCurationClient(config) {
    const baseURL = resolveAtlasBaseURL(config.baseURL);
    return new AtlasCurationClient({
        baseURL,
        timeout: config.timeout,
        getAccessToken: config.getAccessToken,
        retry: config.retry || { retries: 1, retryDelay: 500 },
        logger: config.logger
    });
}
/**
 * Create API client with custom configuration
 */
export function createApiClient(ClientClass, config) {
    return new ClientClass(config);
}
